import { defineStore } from 'pinia'
import JSConfetti from 'js-confetti'
import { REACTIONS } from '@/static/reactions'
import { NativeAudio } from '@capgo/native-audio'
import { Capacitor } from '@capacitor/core'
import message_sent from '../assets/audio/message_sent.wav'
import message_received from '../assets/audio/message_received.wav'
import follow_match from '../assets/audio/follow_match.wav'
import follow_new from '../assets/audio/follow_new.wav'
import post_like from '../assets/audio/post_like.wav'
export interface Alert {
  title?: string
  content?: string
  warning?: string
  isClosable?: boolean
  actions: {
    text: string
    isCloseAction?: boolean
    isMainAction?: boolean
    button?: any
    action: () => Promise<any> | void
  }[]
}

export default defineStore('window', () => {
  const confettis: Ref<JSConfetti | null> = ref(null)
  let isWindowLocked: Ref<Ref<boolean> | null> = ref(null)
  const meta = ref()
  const titles: Ref<Map<string, { value?: string, createdAt?: Date, path?: string[] }>> = ref(new Map())
  const scrolls: Ref<Map<string, number>> = ref(new Map())
  const membersCount = ref(0)
  const isLockNavigation = ref(false)
  const sounds: Ref<Map<string, () => void>> = ref(new Map())
  const isUserActive = ref(true)

  const alert: Ref<Alert | null> = ref(null)

  const debug: Ref<Map<string, any>> = ref(new Map())

  const isInvisible = ref(false)

  const { height: windowHeight, width: windowWidth } = useWindowSize()
  const { y: windowY, x: windowX } = useWindowScroll()
  const { directions: windowDirections } = useScroll(window)
  const { y: mouseY, x: mouseX } = useMouse()

  const reactionQuick: Ref<{
    _id: string
    type?: 'post' | 'message'
    x: number
    y: number
    onStartLoading: () => void
    onEndLoading: () => void
    reacted: (keyof typeof REACTIONS)[]
  } | null> = ref(null)

  const setScroll = (name: string, value: number) => {
    scrolls.value.set(name, value)
  }

  const initConfettis = (value: JSConfetti) => {
    confettis.value = value
  }

  const initScrollLock = (value: any) => {
    isWindowLocked.value = value
  }

  const addConfetti = (params?: {
    confettiRadius?: number,
    confettiNumber?: number,
    confettiColors?: string[],
    emojis?: string[],
    emojiSize?: number,
  }) => {
    if (confettis.value) confettis.value.addConfetti(params)
  }
  
  const lockWindow = (action: boolean) => {
    if (isWindowLocked.value) isWindowLocked.value.value = action
  }

  const getArrivedState = (offset: number) => {
    const isArrived = ref(false)
    const stop = ref()

    const measure = () => {
      isArrived.value = windowY.value > (document.documentElement.scrollHeight - offset - windowHeight.value)
    }

    const pause = () => {
      if (stop.value) stop.value()
        stop.value = null
    }

    const resume = () => {
      stop.value = watchThrottled(windowY, () => measure(), { throttle: 200, immediate: true })
    }

    resume()

    return {
      isArrived, pause, resume, measure
    }
  }

  const initAudio = async () => {
    if (Capacitor.isNativePlatform()) {
      await NativeAudio.configure({
        focus: false,
        background: false
      })
    }
    
    NativeAudio.preload({
      assetId: 'message_received',
      volume: 0.4,
      assetPath: Capacitor.isNativePlatform() ? 'message_received.wav' : message_received
    })
  
    NativeAudio.preload({
      assetId: 'message_sent',
      volume: 0.4,
      assetPath: Capacitor.isNativePlatform() ? 'message_sent.wav' : message_sent
    })
  
    NativeAudio.preload({
      assetId: 'follow_match',
      volume: 0.4,
      assetPath: Capacitor.isNativePlatform() ? 'follow_match.wav' : follow_match
    })
  
    NativeAudio.preload({
      assetId: 'follow_new',
      volume: 0.4,
      assetPath: Capacitor.isNativePlatform() ? 'follow_new.wav' : follow_new
    })

    NativeAudio.preload({
      assetId: 'post_like',
      volume: 0.2,
      assetPath: Capacitor.isNativePlatform() ? 'post_like.wav' : post_like
    })
  }

  const setSound = (
    id: string,
    play: () => void
  ) => {
    sounds.value.set(id, play)
  }

  const playSound = async (id: string) => {
    const isDisabled = await getCookie('disable-sounds') as boolean
    if (!isDisabled) {
      NativeAudio.play({ assetId: id })
    }
  }

  const setAlert = (value: Alert | null) => {
    alert.value = value
  }

  return {
    meta, scrolls, setScroll,
    lockWindow,
    initAudio,
    setAlert,
    alert,
    initScrollLock,
    setSound,
    debug,
    isLockNavigation,
    reactionQuick,
    windowDirections,
    isInvisible,
    getArrivedState,
    membersCount,
    windowHeight, windowWidth, windowX, windowY, mouseX, mouseY,
    addConfetti,
    isUserActive,
    initConfettis,
    titles, playSound
	}
})
